/**
 * Floor hooks.
 *
 * The floor is polled rather than pushed for now — a terminal redraws the room
 * every few seconds. When the KDS lands and brings a WebSocket with it, this
 * becomes a subscription and nothing above it changes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type TableState = 'free' | 'seated' | 'ordered' | 'check_requested' | 'paying' | 'dirty';

export interface FloorTable {
  id: string;
  floor_plan_id: string;
  section_id: string | null;
  label: string;
  seats: number;
  shape: string;
  pos_x: string;
  pos_y: string;
  width: string;
  height: string;
  state: TableState;
  is_occupied: boolean;
  can_seat: boolean;
  /**
   * Set on a table that has been pushed into another one.
   *
   * Such a table borrows its primary's `state`, `is_occupied`, `guest_count`
   * and `seated_minutes` — two tables pushed together are one table to
   * everybody in the room, and drawing the far half free-and-green beside a
   * party physically sitting at it would send a host to seat somebody into an
   * occupied chair. Its own label and position stay its own, because it is
   * still a piece of furniture in a particular place.
   */
  merged_into_id: string | null;
  /** Set on the table others were pushed into, so a plan can draw the join. */
  merged_table_ids: string[];
  /**
   * How many people this unit seats: two four-tops pushed together is one
   * eight. The number a host wants when a party of six walks in.
   *
   * `seats` stays the physical fact — pushing tables together does not build
   * chairs — so use this one for capacity and that one for the furniture.
   * Zero on a merged-away table: it seats nobody on its own and its chairs are
   * already counted on the primary.
   */
  combined_seats: number;
  seated_minutes: number | null;
  guest_count: number | null;
  order_id: string | null;
}

export interface FloorPlan {
  id: string;
  location_id: string;
  name: string;
  is_default: boolean;
  tables: FloorTable[];
}

export function useFloor(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['floor', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<FloorPlan[]>(`/v1/floor?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    // Tables change as fast as service does. Short enough that a host sees a
    // table free up, long enough not to hammer the API from every terminal.
    staleTime: 5_000,
    refetchInterval: 15_000,
  });
}

export function useSeatTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string; guestCount: number }>({
    mutationFn: ({ tableId, guestCount }) =>
      api.post<FloorTable>(`/v1/floor/tables/${tableId}/seat`, { guest_count: guestCount }),
    onSettled: () => invalidateFloor(queryClient),
  });
}

export function useClearTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string }>({
    mutationFn: ({ tableId }) => api.post<FloorTable>(`/v1/floor/tables/${tableId}/clear`),
    onSettled: () => invalidateFloor(queryClient),
  });
}

export function useReleaseTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string }>({
    mutationFn: ({ tableId }) => api.post<FloorTable>(`/v1/floor/tables/${tableId}/release`),
    onSettled: () => invalidateFloor(queryClient),
  });
}

/**
 * The party has asked for the bill.
 *
 * Nothing else in the system knows this happened — a waiter is told at the
 * table — and recording it is what lets everyone else see the table is on its
 * way out before any money moves.
 */
export function useCheckRequested() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string }>({
    mutationFn: ({ tableId }) =>
      api.post<FloorTable>(`/v1/floor/tables/${tableId}/check-requested`),
    onSettled: () => invalidateFloor(queryClient),
  });
}

export function useTransferTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { fromTableId: string; toTableId: string }>({
    mutationFn: ({ fromTableId, toTableId }) =>
      api.post<void>(`/v1/floor/tables/${fromTableId}/transfer`, { to_table_id: toTableId }),
    onSettled: () => invalidateFloor(queryClient),
  });
}

/**
 * Push tables together into one. Professional and above.
 *
 * The answer to a party of six at the door when the room has two four-tops.
 * The joined tables keep their labels and their place on the plan, stop being
 * seatable, and start reading as part of the unit.
 *
 * A table with its own party is refused — move them with `useTransferTable`
 * first, which is a different act with different consequences for turn time.
 * The primary having people at it is fine and is the ordinary case: four sit
 * down, two friends arrive, a table gets pushed over.
 *
 * Refusals name the table to press instead. Show `error.message` verbatim; it
 * is written for somebody standing in front of a party.
 */
export function useMergeTables() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string; tableIds: string[] }>({
    mutationFn: ({ tableId, tableIds }) =>
      api.post<FloorTable>(`/v1/floor/tables/${tableId}/merge`, { table_ids: tableIds }),
    onSettled: () => invalidateFloor(queryClient),
  });
}

/**
 * Pull a pushed-together table back apart. The whole group at once.
 *
 * Not one table at a time: nobody detaches a single table from a run of four
 * mid-service, and offering it would mostly produce mistakes. Refused while
 * anybody is still sitting there — the party is spread across the tables.
 *
 * The tables come apart in whatever state the unit was in, so a group that has
 * just finished service splits into tables that are all dirty. Returning them
 * to free would send a host to an uncleared table.
 */
export function useUnmergeTables() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string }>({
    mutationFn: ({ tableId }) => api.post<FloorTable>(`/v1/floor/tables/${tableId}/unmerge`),
    onSettled: () => invalidateFloor(queryClient),
  });
}

export interface CreateTableInput {
  floor_plan_id: string;
  label: string;
  seats?: number;
  pos_x?: string;
  pos_y?: string;
  /** The section one server works. Set here or not at all — see `useSections`. */
  section_id?: string;
}

export function useCreateTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, CreateTableInput>({
    mutationFn: (input) => api.post<FloorTable>('/v1/floor/tables', input),
    onSettled: () => invalidateFloor(queryClient),
  });
}

export interface Section {
  id: string;
  floor_plan_id: string;
  name: string;
  /** Who is on it tonight. Null until somebody is put on. */
  server_id: string | null;
}

/** The sections of one room, in the order they are worked. */
export function useSections(floorPlanId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['floor', 'sections', floorPlanId],
    queryFn: ({ signal }) =>
      api.get<Section[]>(`/v1/floor/sections?floor_plan_id=${floorPlanId}`, signal),
    enabled: Boolean(floorPlanId),
    staleTime: 60_000,
  });
}

export function useCreateSection() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Section, ApiError, { floorPlanId: string; name: string }>({
    mutationFn: ({ floorPlanId, name }) =>
      api.post<Section>('/v1/floor/sections', {
        floor_plan_id: floorPlanId,
        name,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['floor', 'sections'] });
    },
  });
}

/**
 * Rename a section, or say who is on it tonight.
 *
 * `clearServer` is separate from passing no server, because a missing field
 * cannot mean "nobody is on it" and that is what the end of every service
 * looks like.
 */
export function useUpdateSection() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Section,
    ApiError,
    { sectionId: string; name?: string; serverId?: string; clearServer?: boolean }
  >({
    mutationFn: ({ sectionId, name, serverId, clearServer }) =>
      api.request<Section>(`/v1/floor/sections/${sectionId}`, {
        method: 'PUT',
        body: {
          ...(name === undefined ? {} : { name }),
          ...(serverId === undefined ? {} : { server_id: serverId }),
          ...(clearServer ? { clear_server: true } : {}),
        },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['floor', 'sections'] });
    },
  });
}

/** Move a table into a section, or out of one. Layout, never state. */
/** Rename a table or change what it seats. Either field may be left out. */
export function useUpdateTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string; label?: string; seats?: number }>(
    {
      mutationFn: ({ tableId, ...body }) =>
        api.request<FloorTable>(`/v1/floor/tables/${tableId}`, { method: 'PUT', body }),
      onSettled: () => invalidateFloor(queryClient),
    },
  );
}

/** Take one table away. Refused (409) while it is in use or pushed together. */
export function useRemoveTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (tableId) =>
      api.request<void>(`/v1/floor/tables/${tableId}`, { method: 'DELETE' }),
    onSettled: () => invalidateFloor(queryClient),
  });
}

export function useSetTableSection() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorTable, ApiError, { tableId: string; sectionId: string | null }>({
    mutationFn: ({ tableId, sectionId }) =>
      api.request<FloorTable>(`/v1/floor/tables/${tableId}/section`, {
        method: 'PUT',
        body: { section_id: sectionId },
      }),
    onSettled: () => invalidateFloor(queryClient),
  });
}

/** Put a table somewhere else on the plan. Layout only: nobody's seating changes. */
export function useMoveTable() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { tableId: string; posX: number; posY: number }>({
    mutationFn: ({ tableId, posX, posY }) =>
      api.request<void>(`/v1/floor/tables/${tableId}/position`, {
        method: 'PATCH',
        body: { pos_x: posX.toFixed(2), pos_y: posY.toFixed(2) },
      }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['floor'] }),
  });
}

export function useCreateFloorPlan() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FloorPlan, ApiError, { location_id: string; name: string }>({
    mutationFn: (input) => api.post<FloorPlan>('/v1/floor/plans', input),
    onSettled: () => invalidateFloor(queryClient),
  });
}

/**
 * Take a room away, with its tables. Refused (409) while anybody is sitting at
 * one of them; the message says so.
 */
export function useRemoveFloorPlan() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (floorPlanId) =>
      api.request<void>(`/v1/floor/plans/${floorPlanId}`, { method: 'DELETE' }),
    onSettled: () => invalidateFloor(queryClient),
  });
}

function invalidateFloor(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['floor'] });
  // A table changing usually means a tab changed too.
  void queryClient.invalidateQueries({ queryKey: ['orders'] });
}

const STATE_TEXT: Record<TableState, string> = {
  free: 'Free',
  seated: 'Seated',
  ordered: 'Ordered',
  check_requested: 'Bill asked for',
  paying: 'Paying',
  dirty: 'Needs clearing',
};

export function describeTableState(state: TableState): string {
  return STATE_TEXT[state] ?? state;
}

/**
 * How a table should read at a glance.
 *
 * Time, not category. A host scanning the room needs to see which parties have
 * been sitting longest and which tables are waiting on a runner — the colour
 * says how urgent, never what kind.
 *
 * `null` minutes returns `'occupied'` and never a time-based tone. Turn times
 * are a Professional feature, so on a plan without them every occupied table
 * arrives with no timer — and `?? 0` painted all of them the freshest colour
 * in the ramp, which told a waiter at a glance that every party in the room
 * had just sat down. Confidently, and about a room they can see. A withheld
 * number is not a small number, and nowhere is that worse than in colour on a
 * screen nobody reads twice.
 */
export function tableTone(
  table: FloorTable,
): 'neutral' | 'ok' | 'warn' | 'danger' | 'accent' | 'occupied' {
  if (table.state === 'dirty') return 'warn';
  if (table.state === 'free') return 'neutral';
  if (table.state === 'check_requested' || table.state === 'paying') return 'accent';

  // Occupied, and we cannot say for how long. Distinct from every urgency
  // tone, because it is not a degree of urgency — it is the absence of the
  // measurement the ramp is made of.
  if (table.seated_minutes === null) return 'occupied';

  if (table.seated_minutes >= 120) return 'danger';
  if (table.seated_minutes >= 90) return 'warn';
  return 'ok';
}

export function formatSeatedFor(minutes: number | null): string {
  if (minutes === null) return '';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** A party waiting for a table. */
export interface WaitingParty {
  id: string;
  name: string;
  party_size: number;
  phone: string | null;
  note: string | null;
  /** What the host said at the door. Null when nobody promised anything. */
  quoted_minutes: number | null;
  state: 'waiting' | 'seated' | 'left' | 'no_show' | 'booked';
  added_at: string;
  waited_minutes: number;
  /** Waiting longer than they were told, and not yet aware of it. */
  over_quote: boolean;
  seated_at: string | null;
  table_id: string | null;
  table_label: string | null;
}

/**
 * Who is waiting, longest first.
 *
 * Refetched on a short interval because the number that matters — how long
 * somebody has been standing there — changes without anybody touching the
 * screen. Professional and above; the caller says whether the plan has it, so
 * a 402 does not arrive on a page load.
 */
export function useWaitlist(locationId: string | undefined, { enabled = true } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['floor', 'waitlist', locationId],
    queryFn: ({ signal }) =>
      api.get<WaitingParty[]>(`/v1/floor/waitlist?location_id=${locationId}`, signal),
    enabled: enabled && Boolean(locationId),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useAddToWaitlist() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    WaitingParty,
    ApiError,
    {
      locationId: string;
      name: string;
      partySize: number;
      phone?: string;
      note?: string;
      quotedMinutes?: number;
    }
  >({
    mutationFn: (input) =>
      api.post<WaitingParty>('/v1/floor/waitlist', {
        location_id: input.locationId,
        name: input.name,
        party_size: input.partySize,
        ...(input.phone ? { phone: input.phone } : {}),
        ...(input.note ? { note: input.note } : {}),
        ...(input.quotedMinutes !== undefined ? { quoted_minutes: input.quotedMinutes } : {}),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
  });
}

/** Sit a waiting party down. Opens the table session the same way the floor does. */
export function useSeatFromWaitlist() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<WaitingParty, ApiError, { entryId: string; tableId: string }>({
    mutationFn: ({ entryId, tableId }) =>
      api.post<WaitingParty>(`/v1/floor/waitlist/${entryId}/seat`, { table_id: tableId }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
  });
}

/** They gave up, or they did not answer when called. */
export function useCloseWaitlistEntry() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<WaitingParty, ApiError, { entryId: string; noShow: boolean }>({
    mutationFn: ({ entryId, noShow }) =>
      api.post<WaitingParty>(`/v1/floor/waitlist/${entryId}/close`, { no_show: noShow }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
  });
}
